import React, { createContext, useContext, useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import fflip from 'fflip';

import config from 'config';
import {
  criteria,
  features,
  type FeatureFlagUser,
  type FeatureFlagContextValue,
  type FeatureFlagId,
  type TypedFeaturesForUser,
} from 'support/fflip';
import type { VideoCtvRedux } from 'store-types';
import {
  loadOverrides,
  type FeatureFlagOverrides,
} from './featureFlagOverrides';

// =============================================================================
// Context
// =============================================================================

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

// =============================================================================
// Initialize fflip
// =============================================================================

let isConfigured = false;

function ensureFflipConfigured(): void {
  if (isConfigured) return;

  fflip.config({
    criteria,
    features,
  });

  isConfigured = true;
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to access the feature flag context.
 * Must be used within a FeatureFlagProvider.
 */
export function useFeatureFlags(): FeatureFlagContextValue {
  const context = useContext(FeatureFlagContext);

  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }

  return context;
}

export function useFeatureFlag(featureId: FeatureFlagId): boolean {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(featureId);
}

// =============================================================================
// Provider
// =============================================================================

interface FeatureFlagProviderProps {
  children: React.ReactNode;
}
const initParams = new URLSearchParams(window.location.search);
/**
 * Feature flag provider component.
 * Wraps children with feature flag context based on current user state.
 */
export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [overrides, setOverrides] = useState<FeatureFlagOverrides>({});

  // Select relevant user data from Redux
  const paramountPlusUser = useSelector(
    (state: VideoCtvRedux) => state.auth?.paramountPlus?.user?.data,
  );
  const adobeAuth = useSelector((state: VideoCtvRedux) => state.auth?.adobe?.authN);
  const geoInfo = useSelector((state: VideoCtvRedux) => state.geoInfo?.data);

  useEffect(() => {
    ensureFflipConfigured();
    setOverrides(loadOverrides());
    setIsInitialized(true);
  }, []);

  const user = useMemo<FeatureFlagUser>(() => {
    // Determine environment from config
    const isProd = !!import.meta.env.PROD;

    return {
      paramountPlus: paramountPlusUser
        ? {
            userId: paramountPlusUser.userId,
            subscriber: paramountPlusUser.subscriber,
            userPackage: paramountPlusUser.userPackage,
          }
        : undefined,
      adobe: adobeAuth
        ? {
            userId: adobeAuth.userId,
            mvpd: adobeAuth.mvpd,
          }
        : undefined,
      platform: config.platformName,
      dma: geoInfo?.dma,
      environment: isProd ? 'production' : 'staging',
    };
  }, [paramountPlusUser, adobeAuth, geoInfo]);

  // Evaluate features for the current user, applying overrides
  const contextValue = useMemo<FeatureFlagContextValue>(() => {
    if (!isInitialized) {
      return {
        isActive: () => false,
        isEnabled: () => false,
        features: {},
        user,
        isInitialized: false,
      };
    }

    const evaluatedFeatures = fflip.getFeaturesForUser(user) as TypedFeaturesForUser;

    // Apply overrides on top of evaluated features
    const featuresWithOverrides: TypedFeaturesForUser = {
      ...evaluatedFeatures,
      ...overrides,
    };

    return {
      isActive: () => !!(import.meta.env.VITE_FF_PANEL && import.meta.env.VITE_FF_PANEL !== '0') || !!initParams.has('featureFlagPanel'),
      isEnabled: (featureId: FeatureFlagId) => {
        // Check overrides first
        if (featureId in overrides) {
          return overrides[featureId];
        }
        return fflip.isFeatureEnabledForUser(featureId, user);
      },
      features: featuresWithOverrides,
      user,
      isInitialized: true,
    };
  }, [user, isInitialized, overrides]);

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export default FeatureFlagProvider;
